import { useEffect, useRef, useState } from 'react';
import { apiClient } from '../api/client';

interface GoogleAuthButtonProps {
  mode: 'signin' | 'signup';
  onSuccess: () => void;
  onError: (message: string) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, string | number>
          ) => void;
        };
      };
    };
  }
}

const GOOGLE_SCRIPT_ID = 'google-identity-services-script';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

export default function GoogleAuthButton({ mode, onSuccess, onError }: GoogleAuthButtonProps) {
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return;
    }

    const existing = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.google) {
        setIsReady(true);
      } else {
        existing.addEventListener('load', () => setIsReady(true), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsReady(true);
    script.onerror = () => onError('Unable to load Google sign-in. Please try again later.');
    document.head.appendChild(script);
  }, [onError]);

  useEffect(() => {
    if (!isReady || !window.google || !buttonContainerRef.current || !GOOGLE_CLIENT_ID) {
      return;
    }

    const handleCredentialResponse = async (response: { credential?: string }) => {
      const idToken = response?.credential;
      if (!idToken) {
        onError('Google sign-in did not return a valid credential.');
        return;
      }

      try {
        setIsSubmitting(true);
        onError('');
        const apiResponse = await apiClient.post('/api/v1/users/google-auth', { id_token: idToken });
        localStorage.setItem('token', apiResponse.data.access_token);
        window.dispatchEvent(new CustomEvent('authStateChanged'));
        onSuccess();
      } catch (err: any) {
        const message =
          err?.response?.data?.detail ||
          'Unable to authenticate with Google right now. Please try again.';
        onError(typeof message === 'string' ? message : 'Google authentication failed.');
      } finally {
        setIsSubmitting(false);
      }
    };

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
    });

    buttonContainerRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(buttonContainerRef.current, {
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      width: 320,
      text: mode === 'signup' ? 'signup_with' : 'signin_with',
    });
  }, [isReady, mode, onSuccess, onError]);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div ref={buttonContainerRef} className={`flex justify-center ${isSubmitting ? 'opacity-70' : ''}`} />
      {isSubmitting && (
        <p className="text-center text-xs text-slate-400">Signing in with Google...</p>
      )}
    </div>
  );
}

